import { toaster } from '@/components/ui/toaster';
import { useAnnouncementsQuery } from '@/hooks/queries/use-announcements-query';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { useIntegrationsQuery } from '@/hooks/queries/use-integrations-query';
import api from '@/libs/axiosInstance';
import { PlatformEnum, type Announcement } from '@fluenceur/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DashboardAlertList, { type DashboardAlertDescriptor } from './DashboardAlertList';
import { createDismissHelpers } from './dismissal';

const { isDismissed, dismiss } = createDismissHelpers('announcement_');

// These messages are selected by the server; keep their client copy explicit
// so locale checks can verify the keys used by the dashboard.
const INLINE_ANNOUNCEMENT_COPY: Record<
  string,
  { title: string; description: string; action: string }
> = {
  instagram_reconnect_required: {
    title: 'integrations.instagram.reconnectRequired.title',
    description: 'integrations.instagram.reconnectRequired.description',
    action: 'integrations.instagram.reconnectRequired.action',
  },
};

export default function DashboardAlerts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentBrand } = useCurrentBrandQuery();
  const { data: announcements } = useAnnouncementsQuery(currentBrand?.id ?? null);
  const { data: integrations } = useIntegrationsQuery();
  const [runtimeDismissed, setRuntimeDismissed] = useState<Set<string>>(new Set());
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);

  const needsEmailReconnect = useMemo(
    () =>
      integrations?.some(
        (integration) =>
          (integration.platform === PlatformEnum.Gmail ||
            integration.platform === PlatformEnum.Outlook) &&
          integration.isConnected &&
          integration.needsRefresh
      ) ?? false,
    [integrations]
  );

  const visibleAnnouncements = useMemo(() => {
    return (announcements ?? []).filter(
      (a): a is Extract<Announcement, { kind: 'inline' }> =>
        a.kind === 'inline' &&
        (a.dismissible === false || (!runtimeDismissed.has(a.id) && !isDismissed(a.id)))
    );
  }, [announcements, runtimeDismissed]);

  const handleDismiss = useCallback((id: string) => {
    dismiss(id);
    setRuntimeDismissed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleAction = useCallback(
    async (announcement: Extract<Announcement, { kind: 'inline' }>) => {
      if (!announcement.action) return;

      if (announcement.action.method === 'navigate') {
        navigate(announcement.action.url);
        return;
      }

      if (announcement.action.method === 'redirect') {
        setLoadingActionId(announcement.id);
        try {
          const response = await api.get<{ data: { installUrl: string } }>(announcement.action.url);
          window.location.href = response.data.data.installUrl;
        } catch {
          toaster.create({
            description: t('announcements.actionError'),
            type: 'error',
          });
          setLoadingActionId(null);
        }
      }
    },
    [navigate, t]
  );

  const alerts = useMemo<DashboardAlertDescriptor[]>(() => {
    const items: DashboardAlertDescriptor[] = [];

    if (needsEmailReconnect) {
      items.push({
        id: 'email-reconnect',
        tone: 'warning',
        title: t('announcements.emailReconnect.title'),
        description: t('announcements.emailReconnect.description'),
        actions: [
          {
            id: 'open-integrations',
            label: t('announcements.emailReconnect.action'),
            onClick: () => navigate('/settings/integrations'),
          },
        ],
      });
    }

    for (const announcement of visibleAnnouncements) {
      const copy = INLINE_ANNOUNCEMENT_COPY[announcement.id];
      items.push({
        id: `announcement-${announcement.id}`,
        tone: announcement.type === 'warning' ? 'warning' : 'info',
        title: t(copy?.title ?? announcement.title),
        description: t(copy?.description ?? announcement.description),
        actions: [
          ...(announcement.action
            ? [
                {
                  id: `${announcement.id}-action`,
                  label: t(copy?.action ?? announcement.action.label),
                  loading: loadingActionId === announcement.id,
                  onClick: () => handleAction(announcement),
                },
              ]
            : []),
          ...(announcement.dismissible === false
            ? []
            : [
                {
                  id: `${announcement.id}-dismiss`,
                  label: t('announcements.dismiss'),
                  variant: 'ghost' as const,
                  onClick: () => handleDismiss(announcement.id),
                },
              ]),
        ],
      });
    }

    return items;
  }, [
    handleAction,
    handleDismiss,
    loadingActionId,
    navigate,
    needsEmailReconnect,
    t,
    visibleAnnouncements,
  ]);

  return <DashboardAlertList alerts={alerts} />;
}
